"use client"

import { useState, useMemo } from "react"
import { RotateCcw, Users, ChefHat } from "lucide-react"
import type { Idioma } from "@/lib/landing/idioma"

type TipoServicio = "fast-casual" | "casual" | "fine-dining" | "buffet"

const ratiosServicio: Record<TipoServicio, { sala: number; cocina: number; nombre: string }> = {
  "fast-casual": { sala: 20, cocina: 40, nombre: "Fast Casual" },
  casual: { sala: 12, cocina: 25, nombre: "Casual Dining" },
  "fine-dining": { sala: 6, cocina: 15, nombre: "Fine Dining" },
  buffet: { sala: 25, cocina: 50, nombre: "Buffet" },
}

const defaultState = {
  cubiertosEsperados: 80,
  tipoServicio: "casual" as TipoServicio,
  tieneTerraza: false,
  mesasTerraza: 10,
}

const T = {
  es: {
    titulo: "Datos del servicio",
    limpiar: "Limpiar",
    cubiertosEsperados: "Cubiertos esperados por servicio",
    tipoServicio: "Tipo de servicio",
    ratioActual: (n: number) => `Ratio actual: 1 camarero por ${n} cubiertos`,
    tengoTerraza: "Tengo terraza",
    mesasTerraza: "Mesas en terraza",
    ratiosReferencia: "📊 Ratios de referencia",
    personalNecesario: "Personal necesario",
    personalTotal: "Personal total por servicio",
    para: (cubiertos: number, nombre: string) => `Para ${cubiertos} cubiertos (${nombre})`,
    sala: "Sala",
    camareros: (n: number) => `camarero${n !== 1 ? "s" : ""}`,
    apoyo: (n: number) => ` + ${n} apoyo`,
    cocina: "Cocina",
    cocineros: (n: number) => `cocinero${n !== 1 ? "s" : ""}`,
    recomendaciones: "Recomendaciones",
    encargado: (n: number) => (
      <>
        Con {n} camareros, necesitas un <strong>encargado de sala</strong> o maître
      </>
    ),
    jefeCocina: (n: number) => (
      <>
        Con {n} cocineros, considera un <strong>jefe de cocina</strong> o sous chef
      </>
    ),
    runner: (n: number) => (
      <>
        Terraza con {n} mesas: añade un <strong>runner</strong> o camarero de apoyo
      </>
    ),
    friegaplatos: (
      <>
        Con más de 100 cubiertos, considera un <strong>office/friegaplatos</strong>
      </>
    ),
    tenEnCuenta: "📋 Ten en cuenta",
    consejos: [
      "Estos ratios son orientativos para un servicio fluido",
      "Ajusta según complejidad de carta y tipo de cocina",
      "Fines de semana pueden requerir +20% personal",
      "Incluye descansos y rotaciones en la planificación",
    ],
  },
  en: {
    titulo: "Service details",
    limpiar: "Reset",
    cubiertosEsperados: "Expected covers per service",
    tipoServicio: "Type of service",
    ratioActual: (n: number) => `Current ratio: 1 server per ${n} covers`,
    tengoTerraza: "I have a terrace",
    mesasTerraza: "Tables on the terrace",
    ratiosReferencia: "📊 Benchmark ratios",
    personalNecesario: "Staff you need",
    personalTotal: "Total staff per service",
    para: (cubiertos: number, nombre: string) => `For ${cubiertos} covers (${nombre})`,
    sala: "Front of house",
    camareros: (n: number) => `server${n !== 1 ? "s" : ""}`,
    apoyo: (n: number) => ` + ${n} support`,
    cocina: "Kitchen",
    cocineros: (n: number) => `cook${n !== 1 ? "s" : ""}`,
    recomendaciones: "Recommendations",
    encargado: (n: number) => (
      <>
        With {n} servers, you need a <strong>floor manager</strong> or maître d&apos;
      </>
    ),
    jefeCocina: (n: number) => (
      <>
        With {n} cooks, consider a <strong>head chef</strong> or sous chef
      </>
    ),
    runner: (n: number) => (
      <>
        Terrace with {n} tables: add a <strong>runner</strong> or a support server
      </>
    ),
    friegaplatos: (
      <>
        With more than 100 covers, consider a <strong>kitchen porter</strong>
      </>
    ),
    tenEnCuenta: "📋 Bear in mind",
    consejos: [
      "These ratios are a guide to keeping service running smoothly",
      "Adjust them to the complexity of your menu and type of cooking",
      "Weekends may need around 20% more staff",
      "Build breaks and rotations into your rota",
    ],
  },
}

export default function PersonalNecesarioCalculator({ idioma = "es" }: { idioma?: Idioma }) {
  const t = T[idioma]
  const [cubiertosEsperados, setCubiertosEsperados] = useState(defaultState.cubiertosEsperados)
  const [tipoServicio, setTipoServicio] = useState<TipoServicio>(defaultState.tipoServicio)
  const [tieneTerraza, setTieneTerraza] = useState(defaultState.tieneTerraza)
  const [mesasTerraza, setMesasTerraza] = useState(defaultState.mesasTerraza)

  const results = useMemo(() => {
    const ratios = ratiosServicio[tipoServicio]

    // Cubiertos adicionales por terraza (estimación: 2.5 personas por mesa)
    const cubiertosTerraza = tieneTerraza ? mesasTerraza * 2.5 : 0
    const cubiertosTotal = cubiertosEsperados + cubiertosTerraza

    // Camareros necesarios
    const camarerosBase = cubiertosTotal / ratios.sala
    const camareros = Math.ceil(camarerosBase)

    // Cocineros necesarios
    const cocinerosBase = cubiertosTotal / ratios.cocina
    const cocineros = Math.ceil(cocinerosBase)

    // Extra para terraza (runner o apoyo)
    const apoyoTerraza = tieneTerraza && mesasTerraza > 5 ? 1 : 0

    // Recomendación de jefe de sala
    const necesitaEncargado = camareros >= 3

    // Recomendación de jefe de cocina
    const necesitaJefeCocina = cocineros >= 3

    return {
      ratios,
      cubiertosTotal,
      cubiertosTerraza,
      camareros,
      camarerosBase,
      cocineros,
      cocinerosBase,
      apoyoTerraza,
      necesitaEncargado,
      necesitaJefeCocina,
      totalPersonal: camareros + cocineros + apoyoTerraza,
    }
  }, [cubiertosEsperados, tipoServicio, tieneTerraza, mesasTerraza])

  const reset = () => {
    setCubiertosEsperados(defaultState.cubiertosEsperados)
    setTipoServicio(defaultState.tipoServicio)
    setTieneTerraza(defaultState.tieneTerraza)
    setMesasTerraza(defaultState.mesasTerraza)
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-200">
        {/* Inputs */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">{t.titulo}</h2>
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t.limpiar}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1.5">
              {t.cubiertosEsperados}
            </label>
            <input
              type="number"
              value={cubiertosEsperados || ""}
              onChange={(e) => setCubiertosEsperados(parseInt(e.target.value) || 0)}
              placeholder="80"
              min="1"
              className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-3">
              {t.tipoServicio}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(ratiosServicio) as TipoServicio[]).map((tipo) => (
                <button
                  key={tipo}
                  onClick={() => setTipoServicio(tipo)}
                  className={`py-3 px-4 rounded-xl font-medium text-sm transition-all ${
                    tipoServicio === tipo
                      ? "bg-lime-500 text-white"
                      : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                  }`}
                >
                  {ratiosServicio[tipo].nombre}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              {t.ratioActual(ratiosServicio[tipoServicio].sala)}
            </p>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={tieneTerraza}
                onChange={(e) => setTieneTerraza(e.target.checked)}
                className="w-5 h-5 rounded border-zinc-300 text-lime-500 focus:ring-lime-500"
              />
              <span className="text-sm font-medium text-zinc-700">{t.tengoTerraza}</span>
            </label>
          </div>

          {tieneTerraza && (
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1.5">
                {t.mesasTerraza}
              </label>
              <input
                type="number"
                value={mesasTerraza || ""}
                onChange={(e) => setMesasTerraza(parseInt(e.target.value) || 0)}
                placeholder="10"
                min="1"
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Ratios reference */}
          <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-sky-800 mb-2">{t.ratiosReferencia}</h3>
            <div className="grid grid-cols-2 gap-2 text-sm text-sky-700">
              {(Object.keys(ratiosServicio) as TipoServicio[]).map((tipo) => (
                <div key={tipo} className="flex justify-between">
                  <span>{ratiosServicio[tipo].nombre}:</span>
                  <span className="font-medium">1:{ratiosServicio[tipo].sala}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="p-6 md:p-8 bg-zinc-50 space-y-6">
          <h2 className="text-lg font-semibold text-zinc-900">{t.personalNecesario}</h2>

          {/* Total */}
          <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-6 text-white">
            <p className="text-sm font-medium text-violet-100 mb-1">{t.personalTotal}</p>
            <p className="text-5xl font-bold">{results.totalPersonal}</p>
            <p className="text-sm text-violet-200 mt-1">
              {t.para(results.cubiertosTotal, results.ratios.nombre)}
            </p>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-lime-500" />
                <p className="text-xs text-zinc-500">{t.sala}</p>
              </div>
              <p className="text-3xl font-bold text-zinc-900">{results.camareros}</p>
              <p className="text-xs text-zinc-400">
                {t.camareros(results.camareros)}
                {results.apoyoTerraza > 0 && t.apoyo(results.apoyoTerraza)}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <ChefHat className="w-5 h-5 text-orange-500" />
                <p className="text-xs text-zinc-500">{t.cocina}</p>
              </div>
              <p className="text-3xl font-bold text-zinc-900">{results.cocineros}</p>
              <p className="text-xs text-zinc-400">
                {t.cocineros(results.cocineros)}
              </p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-zinc-900">{t.recomendaciones}</h3>
            
            {results.necesitaEncargado && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-lime-500">✓</span>
                <span className="text-zinc-600">
                  {t.encargado(results.camareros)}
                </span>
              </div>
            )}
            
            {results.necesitaJefeCocina && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-lime-500">✓</span>
                <span className="text-zinc-600">
                  {t.jefeCocina(results.cocineros)}
                </span>
              </div>
            )}

            {tieneTerraza && results.apoyoTerraza > 0 && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-amber-500">⚡</span>
                <span className="text-zinc-600">
                  {t.runner(mesasTerraza)}
                </span>
              </div>
            )}

            {results.cubiertosTotal > 100 && (
              <div className="flex items-start gap-2 text-sm">
                <span className="text-sky-500">💡</span>
                <span className="text-zinc-600">
                  {t.friegaplatos}
                </span>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-lime-50 border border-lime-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-lime-800 mb-2">{t.tenEnCuenta}</h3>
            <ul className="text-sm text-lime-700 space-y-1">
              {t.consejos.map((c) => (
                <li key={c}>• {c}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
